import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import {
  Loader2,
  X,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  CalendarClock,
  Layers,
  Users,
  Megaphone,
  Sparkles,
} from "lucide-react";
import { API, buildHeaders } from "./shared";
import ImportCsvModal from "./ImportCsvModal";

const TYPES = [
  { key: "annual_report", label: "Annual Report Outreach", subtitle: "Quarterly nudges ahead of the annual filing deadline." },
  { key: "renewal", label: "Renewal Reminder", subtitle: "Reach K-12 schools approaching their renewal cycle." },
  { key: "custom", label: "Custom", subtitle: "Define your own audience, cadence, and message." },
];

const STEPS = [
  { id: 1, label: "Type" },
  { id: 2, label: "Audience" },
  { id: 3, label: "Waves" },
  { id: 4, label: "Review" },
];

function StepBar({ active }) {
  return (
    <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: "#e5e7eb" }} data-testid="wizard-stepbar">
      {STEPS.map((s, i) => {
        const done = active > s.id;
        const cur = active === s.id;
        return (
          <React.Fragment key={s.id}>
            <div className="inline-flex items-center gap-1.5" data-testid={`wizard-step-${s.id}`}>
              <div
                className="h-6 w-6 rounded-full inline-flex items-center justify-center text-[11px] font-bold"
                style={{
                  background: done ? "#16a34a" : cur ? "#0f172a" : "#e5e7eb",
                  color: done || cur ? "#fff" : "#64748b",
                }}
              >
                {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : s.id}
              </div>
              <span
                className="text-[11.5px] font-semibold tracking-wide hidden sm:inline"
                style={{ color: cur ? "#0f172a" : "#94a3b8" }}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-[#cbd5e1]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function NewCampaignWizard({ open, onClose, onCreated, session }) {
  const headers = useMemo(() => buildHeaders(session), [session]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  // Step 1
  const [name, setName] = useState("");
  const [campaignType, setCampaignType] = useState("annual_report");
  const [deadline, setDeadline] = useState("");
  const [summary, setSummary] = useState("");
  // Step 2 — audience
  const [audState, setAudState] = useState("NJ");
  const [audCounty, setAudCounty] = useState("");
  const [audAvailable, setAudAvailable] = useState([]);
  const [audSelected, setAudSelected] = useState(new Set());
  const [audLoading, setAudLoading] = useState(false);
  const [counties, setCounties] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  // Step 3 — waves
  const [waveCount, setWaveCount] = useState(3);

  useEffect(() => {
    if (open) {
      setStep(1);
      setName("");
      setCampaignType("annual_report");
      setDeadline("");
      setSummary("");
      setAudState("NJ");
      setAudCounty("");
      setAudSelected(new Set());
      setWaveCount(3);
    }
  }, [open]);

  // Step 2 — fetch audience when filters change
  const loadAudience = useCallback(async () => {
    setAudLoading(true);
    try {
      const params = new URLSearchParams();
      if (audState) params.set("state", audState);
      if (audCounty) params.set("county", audCounty);
      const r = await axios.get(`${API}/api/admin/campaigns/audiences/preview?${params}`, { headers });
      setAudAvailable(r.data.schools || []);
      setAudSelected(new Set((r.data.schools || []).map((s) => s.school_id))); // auto-select all
      // Build county options off of state-only response
      if (!audCounty) {
        const allCounties = Array.from(new Set((r.data.schools || []).map((s) => s.county))).filter(Boolean).sort();
        setCounties(allCounties);
      }
    } catch { toast.error("Failed to load audience"); }
    finally { setAudLoading(false); }
  }, [audState, audCounty, headers]);

  useEffect(() => {
    if (open && step === 2) loadAudience();
  }, [open, step, loadAudience]);

  const toggleSchool = (sid) => {
    setAudSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const selectAll = () => setAudSelected(new Set(audAvailable.map((s) => s.school_id)));
  const selectNone = () => setAudSelected(new Set());

  const canNext = () => {
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return audSelected.size > 0;
    if (step === 3) return waveCount >= 1 && waveCount <= 10;
    return true;
  };

  const buildWaves = () => {
    return Array.from({ length: waveCount }, (_, i) => ({
      name: `Wave ${i + 1}`,
      subject: `${name} — Wave ${i + 1}`,
      body: `Hi {{leader_name}},\n\nWave ${i + 1} of "${name}" for {{school_name}}.\n\n— 589 Labs`,
      calendly_url: "",
    }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/campaigns/wizard`, {
        name: name.trim(),
        campaign_type: campaignType,
        state: audState,
        deadline_iso: deadline || null,
        summary,
        audience: { state: audState, county: audCounty || null, school_ids: Array.from(audSelected) },
        waves: buildWaves(),
      }, { headers });
      toast.success(`Created "${name}"`);
      onCreated && onCreated(r.data);
      onClose && onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[115] flex items-center justify-center bg-black/55 p-3 sm:p-4" data-testid="new-campaign-wizard">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: "#0b1220", color: "#fff" }}>
          <div className="flex items-center gap-2 min-w-0">
            <Megaphone className="h-5 w-5 text-[#C79A3B]" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#C79A3B]">New Campaign</div>
              <div className="text-[15px] sm:text-[16px] font-bold truncate" data-testid="wizard-title">
                {STEPS.find((s) => s.id === step)?.label}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white" data-testid="wizard-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        <StepBar active={step} />

        {/* Body */}
        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1 min-h-0">
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1-body">
              <div className="space-y-2">
                <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8]">Type</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  {TYPES.map((t) => {
                    const active = campaignType === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setCampaignType(t.key)}
                        className="text-left rounded-lg border p-3 transition-colors"
                        style={{
                          background: active ? "#0f172a" : "#fff",
                          color: active ? "#fff" : "#0f172a",
                          borderColor: active ? "#0f172a" : "#e5e7eb",
                        }}
                        data-testid={`wizard-type-${t.key}`}
                      >
                        <div className="text-[13px] font-bold">{t.label}</div>
                        <div className="text-[11.5px] mt-1" style={{ color: active ? "rgba(255,255,255,0.75)" : "#64748b" }}>{t.subtitle}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8] mb-1">Campaign name</div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Q3 Renewal Outreach"
                  className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px]"
                  data-testid="wizard-name-input"
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8] mb-1">Deadline (optional)</div>
                  <input
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px]"
                    data-testid="wizard-deadline-input"
                  />
                </label>
                <label className="block">
                  <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8] mb-1">Summary</div>
                  <input
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="One-line description"
                    className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px]"
                    data-testid="wizard-summary-input"
                  />
                </label>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2-body">
              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8] mb-1">State</div>
                  <select
                    value={audState}
                    onChange={(e) => { setAudState(e.target.value); setAudCounty(""); }}
                    className="h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px]"
                    data-testid="wizard-state-select"
                  >
                    {["NJ", "NY", "PA", "CT"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8] mb-1">County</div>
                  <select
                    value={audCounty}
                    onChange={(e) => setAudCounty(e.target.value)}
                    className="h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] min-w-[160px]"
                    data-testid="wizard-county-select"
                  >
                    <option value="">All counties</option>
                    {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <div className="ml-auto flex items-center gap-2 text-[12px] font-semibold flex-wrap">
                  <button
                    type="button"
                    onClick={() => setImportOpen(true)}
                    className="text-[11.5px] inline-flex items-center gap-1 px-2 h-7 rounded-md border border-dashed text-[#3B4FE4] hover:bg-[#eef2ff]"
                    style={{ borderColor: "#c7d2fe" }}
                    data-testid="wizard-import-csv-btn"
                  >
                    Import CSV instead
                  </button>
                  <span className="px-2 py-1 rounded-md bg-[#0f172a] text-white" data-testid="wizard-audience-counter">
                    {audSelected.size} of {audAvailable.length} selected
                  </span>
                  <button type="button" onClick={selectAll} className="text-[#3B4FE4] hover:underline" data-testid="wizard-audience-all">All</button>
                  <span className="text-[#cbd5e1]">·</span>
                  <button type="button" onClick={selectNone} className="text-[#3B4FE4] hover:underline" data-testid="wizard-audience-none">None</button>
                </div>
              </div>

              {audLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#94a3b8]" /></div>
              ) : audAvailable.length === 0 ? (
                <div className="text-center py-8 text-[#94a3b8] text-[13px]">No schools match this filter.</div>
              ) : (
                <ul className="max-h-[320px] overflow-y-auto divide-y rounded-md border" style={{ borderColor: "#e5e7eb" }} data-testid="wizard-audience-list">
                  {audAvailable.map((s) => {
                    const checked = audSelected.has(s.school_id);
                    return (
                      <li key={s.school_id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#f8fafc] cursor-pointer"
                        onClick={() => toggleSchool(s.school_id)}
                        data-testid={`wizard-audience-row-${s.school_id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleSchool(s.school_id)} className="h-4 w-4" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-[#0f172a] truncate">{s.school_name}</div>
                          <div className="text-[11.5px] text-[#64748b]">{s.city}, {s.state} · {s.county} County</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3-body">
              <div className="rounded-lg border p-4" style={{ borderColor: "#e5e7eb" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="h-4 w-4 text-[#475569]" />
                  <div className="text-[13.5px] font-bold text-[#0f172a]">Number of waves</div>
                </div>
                <p className="text-[12px] text-[#64748b] mb-3">
                  How many message waves should this campaign have? Default templates will be generated — edit each wave's subject and body later in the Compose tab.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={1} max={10}
                    value={waveCount}
                    onChange={(e) => setWaveCount(parseInt(e.target.value, 10))}
                    className="flex-1"
                    data-testid="wizard-wave-count-slider"
                  />
                  <div className="min-w-[44px] text-center rounded-md border border-[#e5e7eb] py-1 px-2 text-[14px] font-bold text-[#0f172a]" data-testid="wizard-wave-count-value">{waveCount}</div>
                </div>
              </div>

              <div className="space-y-2" data-testid="wizard-wave-preview">
                {Array.from({ length: waveCount }, (_, i) => (
                  <div key={i} className="rounded-lg border p-3 flex items-center gap-3" style={{ borderColor: "#e5e7eb" }}>
                    <span className="h-7 w-7 rounded-full inline-flex items-center justify-center text-[11px] font-bold bg-[#0f172a] text-white">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[#0f172a]">Wave {i + 1}</div>
                      <div className="text-[11.5px] text-[#64748b] truncate">{name || "<campaign name>"} — Wave {i + 1}</div>
                    </div>
                    <span className="text-[10.5px] font-bold tracking-[0.06em] uppercase text-[#94a3b8]">Draft</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-4-body">
              <ReviewRow label="Name" value={name} />
              <ReviewRow label="Type" value={TYPES.find((t) => t.key === campaignType)?.label || campaignType} />
              <ReviewRow label="State" value={audState} />
              <ReviewRow label="County" value={audCounty || "All counties"} />
              <ReviewRow label="Deadline" value={deadline || "—"} />
              <ReviewRow label="Schools selected" value={`${audSelected.size} schools`} />
              <ReviewRow label="Waves" value={`${waveCount} wave${waveCount === 1 ? "" : "s"}`} />
              <ReviewRow label="Summary" value={summary || "—"} />
              <div className="rounded-md px-3 py-2 mt-3 text-[11.5px] flex items-center gap-1.5"
                style={{ background: "rgba(199,154,59,0.10)", color: "#92400e" }}>
                <Sparkles className="h-3 w-3" />
                Sends are MOCKED in Phase 2 — no real email leaves on Launch.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 flex items-center justify-between gap-2 border-t" style={{ borderColor: "#e5e7eb" }}>
          <button
            type="button"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1 || busy}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#e5e7eb] text-[12.5px] font-semibold text-[#475569] disabled:opacity-40"
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back
          </button>
          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canNext() || busy}
              className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md text-[12.5px] font-semibold text-white disabled:opacity-50"
              style={{ background: "#0f172a" }}
              data-testid="wizard-next-btn"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy || audSelected.size === 0 || !name.trim()}
              className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md text-[12.5px] font-semibold text-white disabled:opacity-50"
              style={{ background: "#C79A3B" }}
              data-testid="wizard-submit-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Create Campaign
            </button>
          )}
        </div>
      </div>

      <ImportCsvModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => loadAudience()}
        session={session}
      />
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b" style={{ borderColor: "#f1f5f9" }}>
      <span className="text-[11.5px] font-bold tracking-[0.06em] uppercase text-[#94a3b8] shrink-0">{label}</span>
      <span className="text-[13px] text-[#0f172a] text-right truncate" data-testid={`wizard-review-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value || "—"}</span>
    </div>
  );
}
